import { T } from '@tolgee/react';
import { FunctionComponent, ReactNode, useEffect, useState } from 'react';
import { useIntercom } from 'react-use-intercom';
import styled from 'styled-components';

import { usePaywallCtaTracking } from '../../../hooks/usePaywallCtaTracking';
import {
  ArrowIcon,
  BodyText,
  Centered,
  ChatIcon,
  EmailForm,
  FooterDotDivider,
  FooterLinkRow,
  InlineThanksPanel,
  LinkButton,
  ModalBody,
  ModalShell,
  OrangeBtn,
  OrDivider,
  SectionHeading,
  StepCard,
  StepCardGrid,
  StickerHeader,
  ThanksBody,
  ThanksSticker,
  Tip,
} from './primitives';
import {
  isInternational,
  resolveStatRegion,
  StatBlock,
  StatRegion,
} from './stateSpecificStats';
import { INTEREST_FORM_URL, LEARN_MORE_URL, PRICING_URL } from './urls';

// ── Types ─────────────────────────────────────────────────────────────────

/**
 * Where in the product the modal is shown. Controls which concrete state
 * renders inside the modal shell.
 *
 *  - first-run     State 1 · fresh user, confirms a School Plan lesson start.
 *  - email-gate    State 2 · US-only forgiveness step on the 4th attempt.
 *  - hard-block    State 3 · no trials left. US shows email form; intl shows
 *                  pricing CTA. Email submit transitions to the thanks view.
 *  - home          State 4 · homepage sidebar click — info + steps + stats.
 */
export type SchoolPlanTrialState = 'first-run' | 'email-gate' | 'hard-block' | 'home';

interface CommonProps {
  visible: boolean;
  onClose: () => void;
  country: string | undefined;
  region: string | undefined;
  /**
   * Number of School Plan trials the user has left. Only used for the
   * "home"-state badge and heading; other states display fixed copy.
   */
  remaining?: number;
  /** Per-interaction analytics source tag (e.g. "designer-start"). */
  source?: string;
  /** Fired when the user confirms a lesson start. `email` populated from State 2. */
  onStartLesson?: (capturedEmail: string | undefined) => void;
  /** Fired for every captured email (State 2, 3, 4). */
  onEmailSubmit?: (email: string) => void;
}

type Props = CommonProps & { state: SchoolPlanTrialState };

// ── Local styled helpers ──────────────────────────────────────────────────

const LessonStartingHeading = styled.h2`
  font-size: 20px;
  font-weight: 900;
  margin: 0 0 8px;
  letter-spacing: -0.01em;
  font-family: 'Nunito', sans-serif;
`;

const LessonStartingText = styled.p`
  font-size: 12.5px;
  color: rgba(40, 47, 51, 0.7);
  margin: 0;
  line-height: 1.5;
  font-weight: 700;
  font-family: 'Nunito', sans-serif;

  strong {
    color: #282f33;
    font-weight: 900;
  }
`;

// ── Main component ────────────────────────────────────────────────────────

export const SchoolPlanTrialModal: FunctionComponent<Props> = props => {
  const { visible, state, country, source } = props;
  const trackCta = usePaywallCtaTracking();
  const [submittedEmail, setSubmittedEmail] = useState<string | null>(null);
  const [startedEmail, setStartedEmail] = useState<string | undefined>(undefined);
  const [isStarting, setIsStarting] = useState(false);

  // The modal is mounted once by PremiumTestPrepPlaywallProvider and reused
  // across state transitions (e.g. hard-block → home when the user later
  // clicks the sidebar trial counter). Without this reset, a stale "thanks"
  // or "starting lesson" view from a prior state would render on top of the
  // new state's content.
  useEffect(() => {
    setSubmittedEmail(null);
    setStartedEmail(undefined);
    setIsStarting(false);
  }, [state]);

  const intl = isInternational(country);
  const statRegion: StatRegion = resolveStatRegion(props.region, country);
  const ctaCtx = {
    paywallType: 'school-plan-trial' as const,
    source,
    modalState: state,
  };

  const handleEmailSubmit = (email: string) => {
    props.onEmailSubmit?.(email);
    setSubmittedEmail(email);
  };

  const handleStartLesson = (email?: string) => {
    props.onStartLesson?.(email);
    setStartedEmail(email);
    setIsStarting(true);
  };

  // Universal close handler — clears every transient view-state slot before
  // delegating to the parent. Used by every view branch so closing on any
  // path (X button, mask click, ESC, "Back to lesson" CTA) leaves the modal
  // ready to render the primary view on the next reopen.
  const handleClose = () => {
    setSubmittedEmail(null);
    setStartedEmail(undefined);
    setIsStarting(false);
    props.onClose();
  };

  // After a lesson start, show the "Starting your lesson…" confirmation.
  if (isStarting) {
    return (
      <LessonStartingView visible={visible} onClose={handleClose} email={startedEmail} />
    );
  }

  // After email submit on State 3, show the standalone thanks view.
  // State 2 captures the email but proceeds to lesson start instead.
  // State 4 (home) renders the inline thanks panel inside HomeInfoView so the
  // sidebar interaction never swaps in a second modal.
  if (submittedEmail !== null && state === 'hard-block') {
    return (
      <ThanksView
        visible={visible}
        onClose={handleClose}
        email={submittedEmail}
        source={source}
      />
    );
  }

  switch (state) {
    case 'first-run':
      return (
        <FirstRunView
          visible={visible}
          onClose={handleClose}
          onStart={() => handleStartLesson()}
          onLearnMore={() => trackCta('learn-more', ctaCtx)}
        />
      );

    case 'email-gate':
      return (
        <EmailGateView
          visible={visible}
          onClose={handleClose}
          onSubmit={email => {
            props.onEmailSubmit?.(email);
            handleStartLesson(email);
          }}
          onLearnMore={() => trackCta('learn-more', ctaCtx)}
        />
      );

    case 'hard-block':
      return (
        <HardBlockView
          visible={visible}
          onClose={handleClose}
          intl={intl}
          onSubmit={handleEmailSubmit}
          onLearnMore={() => trackCta('learn-more', ctaCtx)}
          onFillOutForm={() => trackCta('fill-out-form', ctaCtx)}
          onSeePricing={() => trackCta('see-pricing', ctaCtx)}
        />
      );

    case 'home':
      return (
        <HomeInfoView
          visible={visible}
          onClose={handleClose}
          intl={intl}
          statRegion={statRegion}
          remaining={props.remaining}
          submittedEmail={submittedEmail}
          onSubmit={handleEmailSubmit}
          onLearnMore={() => trackCta('learn-more', ctaCtx)}
          onFillOutForm={() => trackCta('fill-out-form', ctaCtx)}
          onSeePricing={() => trackCta('see-pricing', ctaCtx)}
        />
      );
  }
};

// ── State 1 · First run ───────────────────────────────────────────────────

interface FirstRunProps {
  visible: boolean;
  onClose: () => void;
  onStart: () => void;
  onLearnMore: () => void;
}

const FirstRunView: FunctionComponent<FirstRunProps> = ({
  visible,
  onClose,
  onStart,
  onLearnMore,
}) => (
  <ModalShell visible={visible} onClose={onClose} width={500}>
    <StickerHeader
      sticker="stars"
      tone="beige"
      badgeIcon="crown"
      badge={
        <T
          keyName="SchoolPlanTrial.Badge.Used"
          defaultValue="{used} of 3 trials used"
          params={{ used: 0 }}
        />
      }
      heading={
        <T keyName="SchoolPlanTrial.FirstRun.Heading" defaultValue="School Plan Trial" />
      }
    />
    <ModalBody>
      <BodyText>
        <T
          keyName="SchoolPlanTrial.FirstRun.Body"
          defaultValue="This lesson is a <strong>School Plan Lesson</strong>. Starting it will use one of your School Plan Trials."
          params={{ strong: <strong /> }}
        />
      </BodyText>
      <OrangeBtn onClick={onStart}>
        <T keyName="SchoolPlanTrial.FirstRun.StartNow" defaultValue="Start Now" />
        <ArrowIcon />
      </OrangeBtn>
      <Centered>
        <LinkButton href={LEARN_MORE_URL} target="_blank" onClick={onLearnMore}>
          <T keyName="BlockedPaywall.LearnMoreHere" defaultValue="Learn more here" />
        </LinkButton>
      </Centered>
    </ModalBody>
  </ModalShell>
);

// ── State 2 · Email gate (US forgiveness) ────────────────────────────────

interface EmailGateProps {
  visible: boolean;
  onClose: () => void;
  onSubmit: (email: string) => void;
  onLearnMore: () => void;
}

const EmailGateView: FunctionComponent<EmailGateProps> = ({
  visible,
  onClose,
  onSubmit,
  onLearnMore,
}) => (
  <ModalShell visible={visible} onClose={onClose} width={500}>
    <StickerHeader
      sticker="rocket"
      tone="orange"
      badgeIcon="crown"
      badge={
        <T
          keyName="SchoolPlanTrial.Badge.Used"
          defaultValue="{used} of 3 trials used"
          params={{ used: 3 }}
        />
      }
      heading={
        <T
          keyName="SchoolPlanTrial.EmailGate.Heading"
          defaultValue="Get Unlimited School Plan Lessons"
        />
      }
    />
    <ModalBody>
      <BodyText>
        <T
          keyName="SchoolPlanTrial.EmailGate.Body"
          defaultValue="You've used your 3 School Plan Trials — but we've got your back! Enter your email to start the lesson now; we'll reach out when you're not in the classroom."
        />
      </BodyText>
      <EmailForm
        onSubmit={onSubmit}
        autoFocus
        ctaLabel={<T keyName="SchoolPlanTrial.EmailGate.Cta" defaultValue="Start Now" />}
      />
      <Centered>
        <LinkButton href={LEARN_MORE_URL} target="_blank" onClick={onLearnMore}>
          <T keyName="BlockedPaywall.LearnMoreHere" defaultValue="Learn more here" />
        </LinkButton>
      </Centered>
      <Tip>
        <T
          keyName="BlockedPaywall.Tip.Principal"
          defaultValue="<strong>Tip:</strong> Invite your principal to observe a lesson. That's what gets their interest."
          params={{ strong: <strong /> }}
        />
      </Tip>
    </ModalBody>
  </ModalShell>
);

// ── State 3 · Hard block ──────────────────────────────────────────────────

interface HardBlockProps {
  visible: boolean;
  onClose: () => void;
  intl: boolean;
  onSubmit: (email: string) => void;
  onLearnMore: () => void;
  onFillOutForm: () => void;
  onSeePricing: () => void;
}

const HardBlockView: FunctionComponent<HardBlockProps> = ({
  visible,
  onClose,
  intl,
  onSubmit,
  onLearnMore,
  onFillOutForm,
  onSeePricing,
}) => (
  <ModalShell visible={visible} onClose={onClose} width={520}>
    <StickerHeader
      sticker="crown"
      tone="lavender"
      badgeIcon="crown"
      badge={
        <T
          keyName="SchoolPlanTrial.Badge.Used"
          defaultValue="{used} of 3 trials used"
          params={{ used: 3 }}
        />
      }
      heading={
        <T
          keyName="SchoolPlanTrial.HardBlock.Heading"
          defaultValue="Get Unlimited School Plan Lessons"
        />
      }
    />
    <ModalBody>
      <BodyText>
        {intl ? (
          <T
            keyName="SchoolPlanTrial.HardBlock.Body.Intl"
            defaultValue="You've used your 3 School Plan Trials. Upgrade to the school plan to keep using School Plan Lessons with your classes."
          />
        ) : (
          <T
            keyName="SchoolPlanTrial.HardBlock.Body.US"
            defaultValue="You've used your 3 School Plan Trials. We would love to help you get unlimited access. Fill out the form or leave your email — we'll reach out."
          />
        )}
      </BodyText>

      {intl ? (
        <a href={PRICING_URL} style={{ textDecoration: 'none' }} onClick={onSeePricing}>
          <OrangeBtn>
            <T keyName="BlockedPaywall.SeePricing" defaultValue="See plans and pricing" />
            <ArrowIcon />
          </OrangeBtn>
        </a>
      ) : (
        <EmailForm onSubmit={onSubmit} autoFocus />
      )}

      <FooterLinkRow>
        {!intl && (
          <>
            <LinkButton href={INTEREST_FORM_URL} target="_blank" onClick={onFillOutForm}>
              <T keyName="BlockedPaywall.FillOutForm" defaultValue="Fill out this form" />
            </LinkButton>
            <FooterDotDivider>·</FooterDotDivider>
          </>
        )}
        <LinkButton href={LEARN_MORE_URL} target="_blank" onClick={onLearnMore}>
          <T keyName="BlockedPaywall.LearnMoreHere" defaultValue="Learn more here" />
        </LinkButton>
      </FooterLinkRow>

      <Tip>
        <T
          keyName="BlockedPaywall.Tip.Principal"
          defaultValue="<strong>Tip:</strong> Invite your principal to observe a lesson. That's what gets their interest."
          params={{ strong: <strong /> }}
        />
      </Tip>
    </ModalBody>
  </ModalShell>
);

// ── State 3 Thanks (after email submit) ──────────────────────────────────

interface ThanksProps {
  visible: boolean;
  onClose: () => void;
  email: string;
  source?: string;
}

const ThanksView: FunctionComponent<ThanksProps> = ({
  visible,
  onClose,
  email,
  source,
}) => {
  const { show, showNewMessages } = useIntercom();
  const trackCta = usePaywallCtaTracking();
  const ctaCtx = {
    paywallType: 'school-plan-trial' as const,
    source,
    modalState: 'hard-block-thanks',
  };

  // Close the modal first, then open the Intercom messenger. See the matching
  // handler in WeeklySessionLimitModal — leaving the modal mounted leaves the
  // messenger occluded (and on routes using `useHideIntercom` it never
  // surfaces at all).
  const handleChat = () => {
    trackCta('chat-with-us', ctaCtx);
    onClose();
    show();
    showNewMessages();
  };

  return (
    <ModalShell visible={visible} onClose={onClose} width={520}>
      <StickerHeader
        sticker="praise"
        tone="cream"
        badgeIcon="crown"
        badge={<T keyName="BlockedPaywall.Thanks.Badge" defaultValue="Thank you" />}
        heading={
          <T
            keyName="BlockedPaywall.Thanks.Heading"
            defaultValue="We'll be in touch soon"
          />
        }
      />
      <ModalBody>
        <BodyText>
          <T
            keyName="BlockedPaywall.Thanks.Body"
            defaultValue="We're so sorry if this interferes with your teaching. We'll reach out to <strong>{email}</strong> soon. Chat with us if you need help now."
            params={{
              email,
              strong: <strong style={{ color: '#E58332' }} />,
            }}
          />
        </BodyText>
        <OrangeBtn dark onClick={handleChat}>
          <ChatIcon />
          <T keyName="BlockedPaywall.Thanks.Chat" defaultValue="Chat with us now" />
        </OrangeBtn>
        <FooterLinkRow>
          <LinkButton
            href={INTEREST_FORM_URL}
            target="_blank"
            onClick={() => trackCta('fill-out-form', ctaCtx)}
          >
            <T keyName="BlockedPaywall.FillOutForm" defaultValue="Fill out this form" />
          </LinkButton>
          <FooterDotDivider>·</FooterDotDivider>
          <LinkButton
            href={LEARN_MORE_URL}
            target="_blank"
            onClick={() => trackCta('learn-more', ctaCtx)}
          >
            <T keyName="BlockedPaywall.LearnMoreHere" defaultValue="Learn more here" />
          </LinkButton>
        </FooterLinkRow>
        <Tip>
          <T
            keyName="BlockedPaywall.Tip.Principal"
            defaultValue="<strong>Tip:</strong> Invite your principal to observe a lesson. That's what gets their interest."
            params={{ strong: <strong /> }}
          />
        </Tip>
      </ModalBody>
    </ModalShell>
  );
};

// ── State 4 · Home info view ─────────────────────────────────────────────

interface HomeInfoProps {
  visible: boolean;
  onClose: () => void;
  intl: boolean;
  statRegion: StatRegion;
  remaining?: number;
  submittedEmail: string | null;
  onSubmit: (email: string) => void;
  onLearnMore: () => void;
  onFillOutForm: () => void;
  onSeePricing: () => void;
}

const HomeInfoView: FunctionComponent<HomeInfoProps> = ({
  visible,
  onClose,
  intl,
  statRegion,
  remaining,
  submittedEmail,
  onSubmit,
  onLearnMore,
  onFillOutForm,
  onSeePricing,
}) => {
  // Badge mirrors the sidebar counter that opened the modal. If caller didn't
  // pass `remaining`, default to the design's "3 of 3" phrasing.
  const badge: ReactNode =
    remaining != null && remaining !== 3 ? (
      <T
        keyName="SchoolPlanTrial.Badge.Left"
        defaultValue="{remaining} {remaining, plural, one {trial} other {trials}} left"
        params={{ remaining }}
      />
    ) : (
      <T
        keyName="SchoolPlanTrial.Badge.Used"
        defaultValue="{used} of 3 trials used"
        params={{ used: 3 }}
      />
    );

  return (
    <ModalShell visible={visible} onClose={onClose} width={580}>
      <StickerHeader
        sticker="rocket"
        tone="orange"
        badgeIcon="crown"
        badge={badge}
        heading={
          <T
            keyName="SchoolPlanTrial.Home.Heading"
            defaultValue="Get Unlimited School Plan Lessons"
          />
        }
      />
      <ModalBody>
        <BodyText>
          {intl ? (
            <T
              keyName="SchoolPlanTrial.Home.Body.Intl"
              defaultValue="On the free plan, you can trial Curipod's School Plan Lessons. To get unlimited access, you need a school plan."
            />
          ) : (
            <T
              keyName="SchoolPlanTrial.Home.Body.US"
              defaultValue="On the free plan, you can trial Curipod's School Plan Lessons. To get unlimited access, you need a school plan — but we know that takes time. So we have a process to support teachers while their school isn't ready yet."
            />
          )}
        </BodyText>

        {!intl && (
          <>
            <StepCardGrid>
              <StepCard n={1}>
                <T
                  keyName="BlockedPaywall.Step1.Invite"
                  defaultValue="Invite your principal to observe the engagement in your classroom"
                />
              </StepCard>
              <StepCard n={2}>
                <T
                  keyName="BlockedPaywall.Step2.FollowUp"
                  defaultValue="We follow up and help you connect it to your school's goals and find budget"
                />
              </StepCard>
            </StepCardGrid>

            {!submittedEmail && (
              <SectionHeading>
                <T
                  keyName="BlockedPaywall.HelpHeading"
                  defaultValue="We can help you out with every step:"
                />
              </SectionHeading>
            )}
          </>
        )}

        {submittedEmail ? (
          <InlineThanksPanel email={submittedEmail} />
        ) : intl ? (
          <a href={PRICING_URL} style={{ textDecoration: 'none' }} onClick={onSeePricing}>
            <OrangeBtn>
              <T
                keyName="BlockedPaywall.SeePricing"
                defaultValue="See plans and pricing"
              />
              <ArrowIcon />
            </OrangeBtn>
          </a>
        ) : (
          <>
            <a
              href={INTEREST_FORM_URL}
              target="_blank"
              rel="noopener noreferrer"
              style={{ textDecoration: 'none' }}
              onClick={onFillOutForm}
            >
              <OrangeBtn>
                <T
                  keyName="BlockedPaywall.FillOutFormCta"
                  defaultValue="Fill out this form to get help"
                />
              </OrangeBtn>
            </a>

            <OrDivider>
              <T keyName="BlockedPaywall.Or" defaultValue="or" />
            </OrDivider>

            <EmailForm onSubmit={onSubmit} />
          </>
        )}

        {!intl && (
          <div style={{ marginTop: 26 }}>
            <SectionHeading style={{ marginBottom: 14 }}>
              <T keyName="BlockedPaywall.DidYouKnow" defaultValue="Did you know?" />
            </SectionHeading>
            <StatBlock statRegion={statRegion} />
          </div>
        )}

        <Centered>
          <LinkButton href={LEARN_MORE_URL} target="_blank" onClick={onLearnMore}>
            <T keyName="BlockedPaywall.LearnMoreHere" defaultValue="Learn more here" />
          </LinkButton>
        </Centered>
      </ModalBody>
    </ModalShell>
  );
};

// ── Lesson starting confirmation (shown after Start Now) ─────────────────

interface LessonStartingProps {
  visible: boolean;
  onClose: () => void;
  email?: string;
}

const LessonStartingView: FunctionComponent<LessonStartingProps> = ({
  visible,
  onClose,
  email,
}) => (
  <ModalShell visible={visible} onClose={onClose} width={440}>
    <ThanksBody>
      <ThanksSticker src="/images/blocked-paywall-stickers/rocket.svg" alt="" />
      <LessonStartingHeading>
        <T
          keyName="BlockedPaywall.LessonStarting.Heading"
          defaultValue="Starting your lesson…"
        />
      </LessonStartingHeading>
      <LessonStartingText>
        {email ? (
          <T
            keyName="BlockedPaywall.LessonStarting.Body.WithEmail"
            defaultValue="Have a great class! We'll reach out to <strong>{email}</strong> after school."
            params={{ email, strong: <strong /> }}
          />
        ) : (
          <T
            keyName="BlockedPaywall.LessonStarting.Body"
            defaultValue="Have a great class!"
          />
        )}
      </LessonStartingText>
    </ThanksBody>
  </ModalShell>
);
